import { useEffect, useMemo, useState } from "react";

import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useEbay from "../../../data/useEbay";
import usePolicyReplacement from "../../../data/usePolicyReplacement";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import ConfirmModal from "../../../components/shared/Confirm/ConfirmModal";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import useNotification from "../../../components/notifications/useNotifications";
import { EbayPolicyKind, IPolicyReplacementResult } from "../../../models/OfferUpdate";

const kindLabels: Record<EbayPolicyKind, string> = {
  [EbayPolicyKind.Postage]: "Postage",
  [EbayPolicyKind.Payment]: "Payment",
  [EbayPolicyKind.Returns]: "Returns",
};

// The last run, so its progress survives leaving the page - the queue carries on regardless.
const LAST_RUN_KEY = "replace-ebay-policy:last-run";

const readLastRun = (): IPolicyReplacementResult | null => {
  try {
    const raw = localStorage.getItem(LAST_RUN_KEY);
    return raw ? (JSON.parse(raw) as IPolicyReplacementResult) : null;
  } catch {
    return null;
  }
};

const writeLastRun = (run: IPolicyReplacementResult | null) => {
  try {
    if (run?.batchId) localStorage.setItem(LAST_RUN_KEY, JSON.stringify(run));
    else localStorage.removeItem(LAST_RUN_KEY);
  } catch {
    // Storage blocked - progress just won't survive a reload
  }
};

// The queue stores eBay's response body as the failure - { responses: [{ errors: [{ message }] }] } or
// { errors: [...] } - so show just the messages. Anything else is shown as it came.
const ebayErrorText = (error: string | null) => {
  if (!error) return "";

  try {
    const body = JSON.parse(error);
    const errors = [...(body?.errors ?? []), ...(body?.responses ?? []).flatMap((r: any) => r?.errors ?? [])];
    const messages = errors.map((e: any) => e?.message).filter(Boolean);
    return messages.length > 0 ? messages.join(" ") : error;
  } catch {
    return error;
  }
};

const selectClasses =
  "mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500";

// Moves every unsold part on one eBay business policy to another. Built for a policy eBay has started
// refusing: live listings on it keep selling, but every edit, relist and returned-part republish fails.
export default function ReplaceEbayPolicy() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { policies } = useEbay();

  const [kind, setKind] = useState<EbayPolicyKind>(EbayPolicyKind.Postage);
  const [fromPolicyId, setFromPolicyId] = useState("");
  const [toPolicyId, setToPolicyId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResultState] = useState<IPolicyReplacementResult | null>(readLastRun);

  const setResult = (run: IPolicyReplacementResult | null) => {
    setResultState(run);
    writeLastRun(run);
  };

  const { inUse, preview, batch, replace } = usePolicyReplacement(
    kind,
    fromPolicyId || undefined,
    toPolicyId || undefined,
    result?.batchId
  );

  useEffect(() => {
    setInfo({
      name: "Replace eBay Policy",
      desc: "Move every unsold part from one eBay policy to another",
    });
  }, [setInfo]);

  // eBay's own list for this kind - the only policies a part can be moved onto.
  const ebayPolicies = useMemo(() => {
    const all = policies.data?.data;
    if (!all) return [];
    if (kind === EbayPolicyKind.Payment) return all.paymentPolicies;
    if (kind === EbayPolicyKind.Returns) return all.returnPolicies;
    return all.fulfilmentPolicies;
  }, [policies.data, kind]);

  const nameOf = (policyId: string, storedName?: string | null) =>
    ebayPolicies.find((p) => p.id === policyId)?.name ?? storedName ?? policyId;

  const fromOptions = inUse.data?.data ?? [];
  const toOptions = ebayPolicies.filter((p) => p.id !== fromPolicyId);
  const fromIsOnEbay = !fromPolicyId || ebayPolicies.some((p) => p.id === fromPolicyId);

  const counts = preview.data?.data;
  const total = counts ? counts.liveListings + counts.otherOffers + counts.categoryDefaults : 0;

  const progress = batch.data?.data;
  const done = progress ? progress.succeeded + progress.failed : 0;
  const failedItems = progress?.items.filter((x) => x.status === "Failed") ?? [];
  const runInProgress = Boolean(result?.batchId) && !progress?.isComplete;

  const changeKind = (value: EbayPolicyKind) => {
    setKind(value);
    setFromPolicyId("");
    setToPolicyId("");
  };

  const run = (limit?: number) => {
    replace.mutate(
      { kind, fromPolicyId, toPolicyId, limit },
      {
        onSuccess: (res) => {
          if (!res.success) {
            addNotification({ primaryText: res.message, variant: "warn" });
            return;
          }

          setResult({ ...res.data, trial: Boolean(limit) });
          addNotification({ primaryText: res.message, variant: "success" });
        },
        onError: (err: any) =>
          addNotification({ primaryText: err?.message ?? "Failed to replace the policy", variant: "error" }),
        onSettled: () => setConfirmOpen(false),
      }
    );
  };

  return (
    <div className="p-2 space-y-6">
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={`Replace ${kindLabels[kind].toLowerCase()} policy?`}
        confirmButtonText="Replace policy"
        message={
          `${nameOf(fromPolicyId)} will be replaced with ${nameOf(toPolicyId)} on ${counts?.liveListings ?? 0} live listing(s), ` +
          `${counts?.otherOffers ?? 0} other listing(s) and ${counts?.categoryDefaults ?? 0} category default(s). ` +
          "Live listings are updated on eBay in the background. Sold parts keep their current policy."
        }
        isLoading={replace.isLoading}
        onConfirm={() => run()}
      />

      <div>
        <h1 className="text-lg font-medium text-gray-900">Replace eBay Policy</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Moves every unsold part on one eBay policy to another. Use it when eBay stops accepting a policy:
          listings on it keep selling, but eBay refuses any change to them, a relist, or listing a returned part again.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 max-w-4xl">
        <label className="block text-sm font-medium text-gray-700">
          Policy type
          <select
            id="replace-policy-kind"
            className={selectClasses}
            value={kind}
            onChange={(e) => changeKind(Number(e.target.value) as EbayPolicyKind)}
            disabled={replace.isLoading}
          >
            {Object.entries(kindLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Replace
          <select
            id="replace-policy-from"
            className={selectClasses}
            value={fromPolicyId}
            onChange={(e) => {
              setFromPolicyId(e.target.value);
              if (batch.data?.data?.isComplete) setResult(null);
            }}
            disabled={inUse.isLoading || replace.isLoading}
          >
            <option value="">{inUse.isLoading ? "Loading…" : "Choose a policy"}</option>
            {fromOptions.map((p) => (
              <option key={p.policyId} value={p.policyId}>
                {nameOf(p.policyId, p.name)} ({p.offers} unsold)
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm font-medium text-gray-700">
          With
          <select
            id="replace-policy-to"
            className={selectClasses}
            value={toPolicyId}
            onChange={(e) => {
              setToPolicyId(e.target.value);
              if (batch.data?.data?.isComplete) setResult(null);
            }}
            disabled={policies.isLoading || replace.isLoading}
          >
            <option value="">{policies.isLoading ? "Loading…" : "Choose a policy"}</option>
            {toOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!fromIsOnEbay && (
        <p className="text-sm text-amber-800 max-w-4xl">
          This policy is no longer on your eBay account, so listings on it will fail on any change until they are moved.
        </p>
      )}

      {fromPolicyId && (
        <div className="max-w-4xl space-y-3">
          {preview.isLoading ? (
            <LoadingWheel />
          ) : counts ? (
            <>
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div className="rounded border border-gray-200 bg-white p-3">
                  <dt className="text-gray-500">Live listings</dt>
                  <dd className="text-lg font-medium text-gray-900">{counts.liveListings}</dd>
                  <dd className="text-xs text-gray-500">Updated on eBay in the background</dd>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                  <dt className="text-gray-500">Other listings</dt>
                  <dd className="text-lg font-medium text-gray-900">{counts.otherOffers}</dd>
                  <dd className="text-xs text-gray-500">Unlisted, ended or returned. Changed straight away</dd>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                  <dt className="text-gray-500">Category defaults</dt>
                  <dd className="text-lg font-medium text-gray-900">{counts.categoryDefaults}</dd>
                  <dd className="text-xs text-gray-500">So new parts stop using it</dd>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                  <dt className="text-gray-500">Left as they are</dt>
                  <dd className="text-lg font-medium text-gray-900">{counts.leftAlone}</dd>
                  <dd className="text-xs text-gray-500">Sold or deleted parts</dd>
                </div>
              </dl>

              {counts.shopifyMappingMissing && (
                <p className="text-sm text-amber-800">
                  The new postage policy has no Shopify delivery profile mapped. Map it under Shopify Shipping Sync,
                  or parts moved onto it will lose their Shopify shipping.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <ButtonInput
                  label="Test with 5"
                  isSubmit={false}
                  onClick={() => run(5)}
                  classes={"!bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50"}
                  disabled={!toPolicyId || counts.liveListings === 0 || replace.isLoading || runInProgress}
                />
                <ButtonInput
                  label="Replace policy"
                  isSubmit={false}
                  onClick={() => setConfirmOpen(true)}
                  classes={undefined}
                  disabled={!toPolicyId || total === 0 || replace.isLoading || runInProgress}
                />
              </div>
              <p className="text-xs text-gray-500">
                Test with 5 updates five live listings on eBay and nothing else, so you can check eBay accepts the new
                policy before replacing it everywhere.
              </p>
              {runInProgress && <p className="text-sm text-gray-500">Wait for the current run to finish before starting another.</p>}
              {total === 0 && <p className="text-sm text-gray-500">No unsold parts or category defaults are on this policy.</p>}
            </>
          ) : null}
        </div>
      )}

      {result?.batchId && (
        <div className="max-w-4xl rounded border border-gray-200 bg-white p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-900">
              {result.trial
                ? progress?.isComplete
                  ? "Test finished"
                  : "Testing on 5 live listings…"
                : progress?.isComplete
                  ? "Live listings updated"
                  : "Updating live listings on eBay…"}
            </span>
            <span className="tabular-nums text-gray-600">
              {done} / {progress?.total ?? result.liveListings}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-primary-600 transition-all"
              style={{ width: `${progress?.total ? (done / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-gray-500">
            {progress?.succeeded ?? 0} updated, {progress?.failed ?? 0} failed.{" "}
            {progress?.isComplete && result.trial
              ? progress.failed === 0
                ? "eBay accepted the new policy. Use Replace policy to move everything else."
                : "Check the errors below before replacing the policy everywhere."
              : progress?.isComplete
              ? "Listings that failed are still on the old policy - run it again to retry them."
              : "You can leave this page. The update carries on, and the progress is here when you come back."}
          </p>
          {failedItems.length > 0 && (
            <ul className="max-h-48 overflow-y-auto text-xs text-red-700 space-y-1">
              {failedItems.map((item) => (
                <li key={item.id}>
                  <span className="font-semibold">{item.sku ?? item.partOfferId}:</span> {ebayErrorText(item.errorMessage)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
